import type { MatchProfileDetail, MatchReaction } from "../../services/matchesApi";
import {
  formatPersonalityType,
  getStyleSlug,
  normalizePersonalityEnum,
} from "../homeowner-profile/utils";
import { quizColorMap } from "../../components/personality-quiz/utils";
import { Pages } from "../../utils";
import { ANALYTICS_STORAGE_KEY, getBaseUrl } from "./constants";
import type {
  HomeownerStyleSnapshot,
  MatchRecord,
  PaginationItem,
  RateStarState,
} from "./types";
import type { MatchReactionOverrideMap } from "../../utils/match-reaction-cache";

export const trackMatchesEvent = (
  event: string,
  payload: Record<string, string | number | boolean | null | undefined>
) => {
  try {
    const stored = localStorage.getItem(ANALYTICS_STORAGE_KEY);
    const existing = stored ? (JSON.parse(stored) as any[]) : [];
    const next = [
      ...existing,
      {
        event,
        payload,
        timestamp: new Date().toISOString(),
      },
    ].slice(-200);
    localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore analytics storage failures
  }
};

export const normalizeReaction = (
  reaction: MatchReaction | null | undefined
): MatchReaction | null => {
  if (reaction === "love_like") return "love";
  return reaction ?? null;
};

const isMatchReaction = (value: unknown): value is MatchReaction => {
  return (
    value === "love" ||
    value === "like" ||
    value === "love_like" ||
    value === "not_for_me" ||
    value === null
  );
};

export const getCounterpartReaction = (match: MatchRecord): MatchReaction | null => {
  const snapshot = match.profileSnapshot as Record<string, unknown> | null | undefined;
  const candidates: unknown[] = [
    match.counterpartReaction,
    (match as Record<string, unknown>).counterpart_reaction,
    (match as Record<string, unknown>).counterPartReaction,
    (match as Record<string, unknown>).theirReaction,
    (match as Record<string, unknown>).otherReaction,
    snapshot?.counterpartReaction,
    snapshot?.counterpart_reaction,
    snapshot?.counterPartReaction,
    snapshot?.theirReaction,
    snapshot?.otherReaction,
  ];

  for (const candidate of candidates) {
    if (isMatchReaction(candidate)) {
      return normalizeReaction(candidate);
    }
  }

  return null;
};

export const shouldShowSelectPerfectMatch = ({
  match,
  isHomeowner,
  hasActiveMatch,
}: {
  match: MatchRecord;
  isHomeowner: boolean;
  hasActiveMatch: boolean;
}) => {
  if (!isHomeowner) return false;
  if (hasActiveMatch) return false;
  if (Boolean(match.isMatchMade)) return false;

  const homeownerLoved = hasLoveReaction(match.reaction ?? null);
  const counterpartLoved = hasLoveReaction(getCounterpartReaction(match));
  return Boolean(match.isMutualLove) || (homeownerLoved && counterpartLoved);
};

export const hasLoveReaction = (reaction: MatchReaction | null | undefined) =>
  normalizeReaction(reaction) === "love";

export const hasLikeReaction = (reaction: MatchReaction | null | undefined) =>
  normalizeReaction(reaction) === "like";

export type CounterpartReactionBadgeTone = "love" | "like" | "not_for_me";

export const getCounterpartReactionBadge = (
  reaction: MatchReaction | null | undefined
) => {
  const normalized = normalizeReaction(reaction);
  if (normalized === "love") {
    return { label: "They Loved me", tone: "love" as const };
  }
  if (normalized === "like") {
    return { label: "They Liked me", tone: "like" as const };
  }
  if (normalized === "not_for_me") {
    return { label: "Not for me", tone: "not_for_me" as const };
  }
  return null;
};

export const getNextReaction = (
  current: MatchReaction | null,
  next: MatchReaction
): MatchReaction => {
  const normalizedCurrent = normalizeReaction(current);
  if (next === null) return null;
  if (next === "not_for_me") {
    return normalizedCurrent === "not_for_me" ? null : "not_for_me";
  }
  if (next === "love") {
    return normalizedCurrent === "love" ? null : "love";
  }
  if (next === "like") {
    return normalizedCurrent === "like" ? null : "like";
  }
  if (next === "love_like") {
    return "love";
  }
  return normalizedCurrent ?? null;
};

export const getCompatibilityStars = (
  rating: number | null | undefined
): RateStarState[] => {
  if (typeof rating !== "number" || Number.isNaN(rating)) {
    return Array.from({ length: 5 }, () => "empty" as const);
  }
  const clamped = Math.max(0, Math.min(5, rating));
  const rounded = Math.round(clamped * 2) / 2;
  const full = Math.floor(rounded);
  const hasHalf = rounded - full === 0.5;
  return Array.from({ length: 5 }, (_, index) => {
    if (index < full) return "filled";
    if (index === full && hasHalf) return "half";
    return "empty";
  });
};

export const getCompatibilityRatingFromMatchScore = (
  score: number | null | undefined
) => {
  if (typeof score !== "number" || Number.isNaN(score)) {
    return null;
  }
  return Math.max(0, Math.min(5, score / 20));
};

export const getCompatibilityLabel = (rating: number | null | undefined) => {
  if (!rating) return "";
  if (rating >= 4.5) return "Strong";
  if (rating >= 3.5) return "Good";
  if (rating >= 2.5) return "Fair";
  return "Low";
};

export const getMatchScoreValue = (value: number | null | undefined) =>
  typeof value === "number" ? Math.max(0, Math.min(100, value)) : 0;

export const getMatchScoreColors = (value: number) => {
  if (value >= 80) {
    return { start: "#1fb365", end: "#8fe05a" };
  }
  if (value >= 70) {
    return { start: "#f08a2a", end: "#ffd36a" };
  }
  if (value >= 60) {
    return { start: "#f4a81c", end: "#f7e45b" };
  }
  return { start: "#d63b39", end: "#f56b5d" };
};

export const formatPageNumber = (value: number) =>
  value < 10 ? `0${value}` : `${value}`;

export const buildPaginationItems = (
  current: number,
  total: number
): PaginationItem[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }
  if (current <= 3) {
    return [1, 2, 3, 4, 5, "ellipsis", total];
  }
  if (current >= total - 2) {
    return [1, "ellipsis", total - 4, total - 3, total - 2, total - 1, total];
  }
  return [1, "ellipsis", current - 1, current, current + 1, "ellipsis", total];
};

export const formatDistance = (distance: number | null | undefined) => {
  if (typeof distance !== "number") return "Distance unavailable";
  return `${Math.round(distance)} miles from you`;
};

export const getLocationLabel = (match: MatchRecord) => {
  const snapshot = match.profileSnapshot as Record<string, unknown> | null | undefined;
  const snapshotLocation =
    (snapshot?.location as string | undefined) ||
    (snapshot?.city as string | undefined) ||
    "";
  return snapshotLocation || "Location unavailable";
};

export const toStringCandidate = (value: unknown) => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return null;
};

export const getProfileId = (match: MatchRecord) => {
  const snapshot = match.profileSnapshot as Record<string, unknown> | null | undefined;
  return (
    match.profileId ||
    (snapshot?.profileId as string | undefined) ||
    (snapshot?.designerId as string | undefined) ||
    null
  );
};

export const getMatchDisplayId = (
  matchId: string | null | undefined,
  viewerAccountType?: string | null
) => {
  const rawMatchId = (matchId ?? "").trim();
  if (!rawMatchId) return "";

  const normalizedViewerType = (viewerAccountType ?? "").toLowerCase();
  const prefixedAliasMatch = rawMatchId.match(/^(designer|homeowner|match)_(.+)$/i);

  if (prefixedAliasMatch) {
    const aliasSuffix = prefixedAliasMatch[2];

    if (normalizedViewerType === "homeowner") {
      return `designer_${aliasSuffix}`;
    }

    if (normalizedViewerType === "designer") {
      return `homeowner_${aliasSuffix}`;
    }

    const normalizedPrefix = prefixedAliasMatch[1].toLowerCase();
    if (normalizedPrefix === "designer") return `designer_${aliasSuffix}`;
    if (normalizedPrefix === "homeowner") return `homeowner_${aliasSuffix}`;
    return `designer_${aliasSuffix}`;
  }

  if (/^match_(?!h)/i.test(rawMatchId)) {
    return rawMatchId.replace(/^match_/i, "designer_");
  }

  if (/^match_h/i.test(rawMatchId)) {
    return rawMatchId.replace(/^match_h/i, "homeowner_");
  }

  if (/^designer_/i.test(rawMatchId)) {
    return rawMatchId.replace(/^designer_/i, "designer_");
  }

  if (/^homeowner_/i.test(rawMatchId)) {
    return rawMatchId.replace(/^homeowner_/i, "homeowner_");
  }

  return rawMatchId;
};

export const getDesignerBioLookupId = (match: MatchRecord) => {
  const snapshot = match.profileSnapshot as Record<string, unknown> | null | undefined;
  return (
    toStringCandidate(match.profileId) ||
    toStringCandidate(snapshot?.profileId) ||
    toStringCandidate(snapshot?.designerId) ||
    toStringCandidate((match as Record<string, unknown>).designerId) ||
    toStringCandidate((match as Record<string, unknown>).aliasId) ||
    toStringCandidate((match as Record<string, unknown>).designerAliasId) ||
    toStringCandidate((match as Record<string, unknown>).matchId) ||
    toStringCandidate((match as Record<string, unknown>).id) ||
    toStringCandidate((match as Record<string, unknown>).userId) ||
    toStringCandidate((match as Record<string, unknown>).wixMemberId) ||
    toStringCandidate(snapshot?.aliasId) ||
    toStringCandidate(snapshot?.designerAliasId) ||
    toStringCandidate(snapshot?.matchId) ||
    toStringCandidate(snapshot?.id) ||
    toStringCandidate(snapshot?.userId) ||
    toStringCandidate(snapshot?.wixMemberId) ||
    null
  );
};

export const getMatchCandidateIds = (match: MatchRecord) => {
  const snapshot = match.profileSnapshot as Record<string, unknown> | null | undefined;
  const candidates = [
    match.profileId,
    match.matchId,
    (match as Record<string, unknown>).designerId,
    (match as Record<string, unknown>).aliasId,
    (match as Record<string, unknown>).designerAliasId,
    (match as Record<string, unknown>).userId,
    (match as Record<string, unknown>).wixMemberId,
    (match as Record<string, unknown>).id,
    snapshot?.profileId,
    snapshot?.designerId,
    snapshot?.aliasId,
    snapshot?.designerAliasId,
    snapshot?.userId,
    snapshot?.wixMemberId,
    snapshot?.matchId,
    snapshot?.id,
  ];
  return candidates.map(toStringCandidate).filter(Boolean) as string[];
};

export const resolvePresenceStatusForMatch = (
  match: MatchRecord,
  role: string,
  getPresenceStatus: (
    userId: string | null | undefined,
    role: string | null | undefined
  ) => boolean | undefined
) => {
  const primaryId = getProfileId(match);
  if (primaryId) {
    const status = getPresenceStatus(primaryId, role);
    if (status !== undefined) return status;
  }
  const candidates = getMatchCandidateIds(match);
  for (const candidate of candidates) {
    const status = getPresenceStatus(candidate, role);
    if (status !== undefined) return status;
  }
  return undefined;
};

export const getReactionOverrideForMatch = (
  match: MatchRecord,
  overrides: MatchReactionOverrideMap
) => {
  const candidates = getMatchCandidateIds(match);
  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(overrides, candidate)) {
      return overrides[candidate] ?? null;
    }
  }
  return undefined;
};

export const applyReactionOverrides = (
  matches: MatchRecord[],
  overrides: MatchReactionOverrideMap
) => {
  if (!Object.keys(overrides).length) return matches;
  return matches.map((match) => {
    const override = getReactionOverrideForMatch(match, overrides);
    if (override === undefined) return match;
    return { ...match, reaction: override };
  });
};

export const getProfileSnapshot = (match: MatchRecord) =>
  match.profileSnapshot as Record<string, unknown> | null | undefined;

export const getHomeownerStyleCoverImage = (match: MatchRecord) => {
  const snapshot = getProfileSnapshot(match) as HomeownerStyleSnapshot | null | undefined;
  const styles = snapshot?.homeOwnerStyles?.homeOwnerStyle ?? [];
  const styleValue = styles.find(
    (value): value is string => Boolean(value && value.trim())
  );
  if (!styleValue) return "";
  const primaryStyle = styleValue.includes("+")
    ? styleValue.split("+")[0].trim()
    : styleValue.trim();
  if (!primaryStyle) return "";
  const favoriteLookRaw = snapshot?.homeOwnerStyles?.favoriteLook;
  const parsedFavoriteLook = Number(favoriteLookRaw);
  const favoriteLook =
    Number.isFinite(parsedFavoriteLook) && parsedFavoriteLook > 0
      ? parsedFavoriteLook
      : 1;
  const slug = getStyleSlug(primaryStyle);
  return `${getBaseUrl()}style_images/${slug}/${favoriteLook}.webp`;
};

export const getTopTags = (match: MatchRecord, profile?: MatchProfileDetail | null) => {
  const snapshot = getProfileSnapshot(match);
  return (
    profile?.topMatchTags ||
    match.topMatchTags ||
    ((snapshot?.topMatchTags as string[] | undefined) ?? [])
  );
};

export const getProfileImage = (
  match: MatchRecord,
  profileImages?: { profilePicUrl?: string } | null,
  profile?: MatchProfileDetail | null
) => {
  const snapshot = getProfileSnapshot(match);
  return (
    profileImages?.profilePicUrl ||
    profile?.profilePicUrl ||
    match.profilePicUrl ||
    (snapshot?.profilePicUrl as string | undefined) ||
    ""
  );
};

export const getHeroImage = (
  match: MatchRecord,
  profileImages?: { coverImageUrl?: string; profilePicUrl?: string } | null,
  profile?: MatchProfileDetail | null,
  fallbackProfileImage?: string,
  viewerAccountType?: string | null
) => {
  const snapshot = getProfileSnapshot(match);
  const isDesignerViewer = (viewerAccountType ?? "").toLowerCase() === "designer";
  const homeownerStyleCover = isDesignerViewer ? getHomeownerStyleCoverImage(match) : "";

  if (homeownerStyleCover) {
    return homeownerStyleCover;
  }

  if (isDesignerViewer) {
    const profileHero =
      profile?.heroImageUrl && profile?.heroImageUrl !== profile?.profilePicUrl
        ? profile.heroImageUrl
        : "";
    const profileCover =
      profile?.coverImageUrl && profile?.coverImageUrl !== profile?.profilePicUrl
        ? profile.coverImageUrl
        : "";
    return (
      profileHero ||
      profileCover ||
      match.coverImageUrl ||
      (snapshot?.heroImageUrl as string | undefined) ||
      (snapshot?.coverImageUrl as string | undefined) ||
      ""
    );
  }
  return (
    profileImages?.coverImageUrl ||
    profileImages?.profilePicUrl ||
    profile?.heroImageUrl ||
    profile?.coverImageUrl ||
    match.coverImageUrl ||
    (snapshot?.heroImageUrl as string | undefined) ||
    (snapshot?.coverImageUrl as string | undefined) ||
    fallbackProfileImage ||
    match.profilePicUrl ||
    ""
  );
};

export const getPersonalityStyleResult = (
  match: MatchRecord,
  profile?: MatchProfileDetail | null
) => {
  const snapshot = getProfileSnapshot(match);
  const rawResult =
    profile?.personalityStyleResult ||
    (snapshot?.personalityStyleResult as string | undefined) ||
    (snapshot?.personalityType as string | undefined) ||
    "";
  return formatPersonalityType(rawResult);
};

export const getPersonalityStyleColors = (
  match: MatchRecord,
  profile?: MatchProfileDetail | null
) => {
  const snapshot = getProfileSnapshot(match);
  const rawResult =
    profile?.personalityStyleResult ||
    (snapshot?.personalityStyleResult as string | undefined) ||
    (snapshot?.personalityType as string | undefined) ||
    "";
  const normalized = normalizePersonalityEnum(rawResult.replace(/_/g, " "));
  if (!normalized) return null;
  return quizColorMap[String(normalized)] ?? null;
};

export const getProfileRoute = (
  accountType: string | null | undefined,
  profileId: string
) => {
  const normalized = (accountType ?? "").toLowerCase();
  if (normalized === "designer") {
    return Pages.HOMEOWNER_PROFILE.replace(":homeOwnerId", profileId);
  }
  return Pages.DESIGNER_PREMIUM_PROFILE.replace(":designerId", profileId);
};
